import { ChangeDetectionStrategy, Component, DestroyRef, Injector, OnInit, computed, inject, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { catchError, of, type Observable } from 'rxjs';
import { AlertService } from '../../services/alert.service';
import { ModalService } from '../../services/modal.service';

export interface ExistingDoc {
  id: string;
  name: string;
  url: string;
}

interface ConfirmDeleteCopy {
  title: string;
  message: string;
}

/**
 * Generic file-upload field with a list of existing attachments. Self-contained:
 * injects ModalService + AlertService, owns its own confirmation flow and local
 * list mutation. The caller only supplies the initial list and the delete API
 * call; new-file uploads are captured as `{ pendingFile }` on the form value so
 * the parent's save pipeline can POST after entity create.
 *
 * Usage:
 * ```
 * {
 *   key: 'attachment',
 *   type: 'file-upload-with-history',
 *   props: {
 *     label: 'Attachment',
 *     accept: '.pdf,.doc,.docx',
 *     existingDocuments: [{ id, name, url }, ...],
 *     deleteDoc: (id) => api.deleteDocument(id),
 *     confirmDelete: { title: 'Delete attachment', message: 'Are you sure?' },
 *     deleteSuccessMessage: 'Attachment deleted',
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-formly-file-upload-with-history',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatIconModule, TranslateModule, FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [attr.data-field-id]="field.id" class="flex flex-col gap-3">
      @if (props.label) {
        <span [class]="labelClass()">
          {{ props.label | translate }}
        </span>
      }

      @if (existing().length) {
        <ul class="flex flex-col gap-2 list-none p-0 m-0">
          @for (doc of existing(); track doc.id) {
            <li class="flex items-center gap-2.5 px-3 py-2.5 border border-[var(--mat-sys-outline-variant)] rounded-xl bg-white transition-shadow hover:shadow-[0_4px_12px_rgba(19,32,52,0.06)]">
              <mat-icon class="text-mty-primary">attach_file</mat-icon>
              <a
                class="flex-1 min-w-0 font-base text-sm text-[var(--mat-sys-on-surface)] no-underline whitespace-nowrap overflow-hidden text-ellipsis hover:text-mty-primary"
                [href]="doc.url"
                target="_blank"
                rel="noopener">{{ doc.name }}</a>
              <button
                mat-icon-button
                color="warn"
                type="button"
                (click)="onDelete(doc)"
                [attr.aria-label]="'Delete attachment' | translate">
                <mat-icon>delete</mat-icon>
              </button>
            </li>
          }
        </ul>
      }

      <label
        [class]="dropzoneClass()"
        (dragenter)="onDrag($event, true)"
        (dragover)="onDrag($event, true)"
        (dragleave)="onDrag($event, false)"
        (drop)="onDrop($event)">
        <mat-icon style="font-size: 32px; width: 32px; height: 32px;">cloud_upload</mat-icon>
        <span class="font-alternate text-sm font-medium text-mty-primary">
          {{ 'Drop file or click to select' | translate }}
        </span>
        <span class="font-base text-xs text-mty-primary/70">
          {{ 'Accepted formats' | translate }}@if (props['accept']) { : {{ props['accept'] }} }
        </span>
        @if (pendingFileName(); as name) {
          <span class="font-base text-xs text-mty-primary font-medium">{{ name }}</span>
        }
        <input
          type="file"
          class="absolute inset-0 opacity-0 cursor-pointer"
          [attr.accept]="props['accept'] || '*/*'"
          (change)="onFileChange($event)" />
      </label>
    </div>
  `,
})
export class FileUploadWithHistoryFieldComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private readonly modal = inject(ModalService);
  private readonly alert = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private readonly docs = signal<ExistingDoc[]>([]);
  readonly existing = computed(() => this.docs());
  readonly isDragging = signal(false);
  readonly pendingFileName = signal<string | null>(null);

  ngOnInit(): void {
    const seed = (this.props['existingDocuments'] as ExistingDoc[] | undefined) ?? [];
    this.docs.set(seed);
  }

  onFileChange(ev: Event): void {
    const file = (ev.target as HTMLInputElement).files?.[0] ?? null;
    this.setPendingFile(file);
  }

  onDrag(ev: DragEvent, active: boolean): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.isDragging.set(active);
  }

  onDrop(ev: DragEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.isDragging.set(false);
    const file = ev.dataTransfer?.files?.[0] ?? null;
    this.setPendingFile(file);
  }

  private setPendingFile(file: File | null): void {
    this.pendingFileName.set(file?.name ?? null);
    this.formControl.setValue({ pendingFile: file });
    this.formControl.markAsDirty();
  }

  dropzoneClass(): string {
    const base = 'relative flex flex-col items-center justify-center gap-1.5 px-5 py-7 border-2 border-dashed rounded-2xl cursor-pointer transition-colors text-center';
    if (this.isInvalid()) {
      return `${base} border-mty-danger bg-mty-danger/5 text-mty-danger hover:bg-mty-danger/10`;
    }
    const idle = 'text-mty-primary border-mty-primary/45 bg-mty-primary/5 hover:bg-mty-primary/10 hover:border-mty-primary';
    const drag = 'text-mty-primary border-mty-primary bg-mty-primary/10';
    return `${base} ${this.isDragging() ? drag : idle}`;
  }

  isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  labelClass(): string {
    const base = 'inline-block font-alternate text-xs font-semibold tracking-wider uppercase mb-1';
    return this.isInvalid()
      ? `${base} text-mty-danger`
      : `${base} text-[var(--mat-sys-on-surface-variant)]`;
  }

  async onDelete(doc: ExistingDoc): Promise<void> {
    const deleteFn = this.props['deleteDoc'] as
      | ((id: string) => Observable<unknown>)
      | undefined;
    if (!deleteFn) return;

    const copy = this.props['confirmDelete'] as ConfirmDeleteCopy | undefined;
    if (copy) {
      const ok = await this.modal
        .Confirm(copy.title, copy.message, {}, [], {}, false)
        .catch(() => false);
      if (!ok) return;
    }

    runInInjectionContext(this.injector, () => {
      deleteFn(doc.id)
        .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          if (res === null) return;
          this.docs.update((list) => list.filter((d) => d.id !== doc.id));
          const msg = this.props['deleteSuccessMessage'] as string | undefined;
          if (msg) this.alert.success(msg);
        });
    });
  }
}
